import { PhoneNumberInput } from '@neo/primitives/phone-number-input'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { useQuery } from '@tanstack/react-query'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { isEmpty, toLower, trim, uniqBy } from 'lodash'
import { useState } from 'react'
import { useNavigate, useParams } from 'router-v6'
import { queryClient } from 'v2/configs/rq-setup'
import { EmptyState } from 'v2/shared/components/empty-state'
import { SuspenseLoader } from 'v2/shared/components/suspense-loader'
import { settingsService } from 'v2/shared/services/settings-service'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { ConvChannels } from 'v2/types/api/conversations'
import type { RInternalNumber } from 'v2/types/api/settings'
import { useTransferMutations } from '../../mutations/conversations'
import { getFormattedContactName } from '../../utils'
import { InternalContactItem } from './internal-contact-item'
import { TransferButtons } from './transfer-buttons'
import type { TransferTabsProps } from './types'
import { getConversationForTransfer } from './utils'

export const InternalContactsList = ({ onTransferSuccess }: TransferTabsProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const { conversationId } = useParams()
  const {
    data: contacts,
    isLoading,
    error,
  } = useQuery({
    queryKey: getSettingsQueryKeys().internalNumbers,
    queryFn: async () => (await settingsService.listInternalNumbers({})).data,
    select: (data) => uniqBy(data, 'phone_number'),
  })

  const { transfer } = useTransferMutations()
  const navigate = useNavigate()

  const getFilteredContacts = () => {
    if (!contacts || isEmpty(contacts)) return []

    const normalizedSearch = toLower(trim(searchTerm))
    if (!normalizedSearch) return contacts

    return contacts.filter((contact) => {
      const fullName = toLower(`${contact.first_name} ${contact.last_name}`.trim())
      const phoneNumber = toLower(contact.phone_number)

      return fullName.includes(normalizedSearch) || phoneNumber.includes(normalizedSearch)
    })
  }

  const handleTransfer = (contact: RInternalNumber, type: 'warm' | 'cold') => {
    const conversation = getConversationForTransfer(conversationId, ConvChannels.CALL)
    if (!conversation.child?.channel || !conversation.parent?.contact_uuid) return

    transfer.mutate(
      {
        channel: conversation.child?.channel,
        transferType: type,
        contactUuid: conversation.parent?.contact_uuid,
        identifier: contact.phone_number,
        agentOrTeam: 'external number',
        member: contact.phone_number,
      },
      {
        onSuccess: () => {
          onTransferSuccess({
            name: `${contact.first_name}${contact.last_name ? ` ${contact.last_name}` : ''}`,
            identifier: contact.phone_number.replace('+', ''),
            type,
            participantType: 'external',
          })
        },
      },
    )
  }

  const handleDirectNumberTransfer = (type: 'warm' | 'cold') => {
    const conversation = getConversationForTransfer(conversationId, ConvChannels.CALL)
    if (!conversation.child?.channel || !conversation.parent?.contact_uuid) return

    transfer.mutate(
      {
        channel: conversation.child?.channel,
        transferType: type,
        contactUuid: conversation.parent?.contact_uuid,
        identifier: searchTerm,
        agentOrTeam: 'external number',
        member: searchTerm,
      },
      {
        onSuccess: () => {
          onTransferSuccess({
            name: searchTerm,
            identifier: searchTerm.replace('+', ''),
            type,
            participantType: 'external',
          })
        },
      },
    )
  }

  if (isLoading)
    return (
      <div className="relative h-[300px]">
        <SuspenseLoader text="Fetching internal contacts..." />
      </div>
    )

  if (error)
    return (
      <EmptyState
        type="app_error"
        text="Error fetching internal contacts"
        actions={[
          {
            text: 'Retry',
            onClick: () => {
              queryClient.invalidateQueries({
                queryKey: getSettingsQueryKeys().internalNumbers,
              })
            },
          },
        ]}
      />
    )

  const filteredContacts = getFilteredContacts()

  return (
    <div>
      <div className="px-1 mb-2">
        <PhoneNumberInput
          placeholder="Enter a number or name"
          className=""
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="input-internal-contact-search"
          allowText
          autoDetectCountry={false}
        />
      </div>
      <ScrollArea className="flex max-h-[300px] flex-col overflow-y-auto -mr-4">
        <div className="pr-4">
          {filteredContacts.map((contact) => (
            <InternalContactItem
              key={`${contact.phone_number}-${contact.first_name}-${contact.last_name}`}
              contact={contact}
              onTransfer={(type) => handleTransfer(contact, type)}
              isDisabled={transfer.isPending}
            />
          ))}
          {filteredContacts.length === 0 && !searchTerm && (
            <EmptyState
              type="empty_app_data"
              text="No contacts found"
              actions={[
                {
                  text: 'Add a contact',
                  onClick: () => {
                    navigate('/forwarding-contacts/internal-contacts')
                  },
                },
              ]}
            />
          )}
          {filteredContacts.length === 0 && searchTerm && (
            <div className="text-s-400 text-tertiary truncate mt-2 p-2">
              {!isValidPhoneNumber(searchTerm) && <div>No contacts found</div>}
              {isValidPhoneNumber(searchTerm) && (
                <div className="flex flex-col gap-2 justify-between">
                  <div className="text-p-600">Invite {getFormattedContactName(searchTerm)} ?</div>
                  <TransferButtons
                    onTransfer={handleDirectNumberTransfer}
                    isDisabled={transfer.isPending}
                    prefix="direct-"
                    alwaysVisible
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
